import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import { applicationDetailMapping, applicationDetailsHeaderMapping } from "./detailMockData.js";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import './AppliedApplicationDetails.styles.scss'
import { formatApplicationDetailsData } from "./helpers.js";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import DeleteDialog from "../DeleteDialog/DeleteDialog.component.js";
import { APP_LABEL } from "../../../translations/index.js";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

const STATUS_ICON = {
    'Applied':     <AssignmentTurnedInIcon className="tl-icon tl-icon--applied" />,
    'In-progress': <AccessTimeIcon         className="tl-icon tl-icon--inprogress" />,
    'In Progress': <AccessTimeIcon         className="tl-icon tl-icon--inprogress" />,
    'Completed':   <CheckCircleIcon        className="tl-icon tl-icon--completed" />,
    'Cancelled':   <CancelIcon             className="tl-icon tl-icon--cancelled" />,
    'Rejected':    <CancelIcon             className="tl-icon tl-icon--rejected" />,
};

const DEFAULT_FALLBACK = (status, applicantName) => [
    { status: 'Applied', timestamp: null, updatedBy: applicantName, comment: 'Application submitted successfully.' },
    ...(status !== 'Applied' ? [{ status, timestamp: null, updatedBy: 'CSC Owner', comment: '' }] : []),
];

const buildHistory = (app) => {
    if (app?.statusHistory?.length) return app.statusHistory;
    const applicantName = app?.applicant?.name || app?.applicant?.email || 'Applicant';
    return DEFAULT_FALLBACK(app?.serviceStatus || 'Applied', applicantName);
};

const fmtTs = (ts) => {
    if (!ts) return null;
    try {
        return new Date(ts).toLocaleString('en-IN', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return ts; }
};

const StatusTimeline = ({ app }) => {
    const history = buildHistory(app);
    const lastIdx = history.length - 1;
    return (
        <div className="tl-root">
            <div className="tl-section-label">Application Timeline</div>
            <div className="tl-track">
                {history.map((entry, idx) => {
                    const isCurrent = idx === lastIdx;
                    const isCancelled = entry.status === 'Cancelled';
                    return (
                        <div key={idx} className={`tl-step${isCurrent ? ' tl-step--current' : ''}${isCancelled ? ' tl-step--cancelled' : ''}`}>
                            <div className="tl-step__icon-col">
                                <div className="tl-step__icon-wrap">
                                    {STATUS_ICON[entry.status] || <RadioButtonUncheckedIcon className="tl-icon" />}
                                </div>
                                {idx < lastIdx && <div className="tl-step__line" />}
                            </div>
                            <div className="tl-step__body">
                                <div className="tl-step__header">
                                    <span className={`tl-step__status tl-step__status--${entry.status?.toLowerCase().replace(/[^a-z]/g, '')}`}>
                                        {entry.status}
                                    </span>
                                    {isCurrent && <span className="tl-step__badge">Current</span>}
                                </div>
                                {entry.comment && <p className="tl-step__comment">{entry.comment}</p>}
                                <div className="tl-step__meta">
                                    {fmtTs(entry.timestamp) && <span>{fmtTs(entry.timestamp)}</span>}
                                    {entry.updatedBy && <span className="tl-step__by">· {entry.updatedBy}</span>}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Review helpers ────────────────────────────────────────────────────────────
const REVIEW_KEY = 'cscReviews';

const getReviewsStore = () => {
    try { return JSON.parse(localStorage.getItem(REVIEW_KEY) || '{}'); }
    catch { return {}; }
};

const getReviewForCenter = (centerKey) => {
    return getReviewsStore()[centerKey] || null;
};

const saveReview = (centerKey, stars, comment, centerName) => {
    const store = getReviewsStore();
    const existing = store[centerKey] || { avgRating: 0, count: 0, reviews: [] };
    const newReview = {
        rating: stars,
        comment: comment.trim(),
        date: new Date().toISOString(),
        centerName,
    };
    const newCount = existing.count + 1;
    const newAvg = Math.round(((existing.avgRating * existing.count + stars) / newCount) * 10) / 10;
    store[centerKey] = {
        avgRating: newAvg,
        count: newCount,
        userReview: newReview,
        reviews: [...(existing.reviews || []), newReview],
    };
    localStorage.setItem(REVIEW_KEY, JSON.stringify(store));

    // Also update the shared cscRatings so the map reflects it
    try {
        const ratingsStore = JSON.parse(localStorage.getItem('cscRatings') || '{}');
        const existing2 = ratingsStore[centerKey] || { avgRating: 0, count: 0 };
        const newCount2 = existing2.count + 1;
        ratingsStore[centerKey] = {
            avgRating: Math.round(((existing2.avgRating * existing2.count + stars) / newCount2) * 10) / 10,
            count: newCount2,
            userRating: stars,
        };
        localStorage.setItem('cscRatings', JSON.stringify(ratingsStore));
    } catch { /* ignore */ }

    return store[centerKey];
};

const fmtReviewDate = (iso) => {
    try {
        return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return ''; }
};

// ── CscReview component ───────────────────────────────────────────────────────
const CscReview = ({ app }) => {
    const centerName = app?.cscCenter?.cscCenterName || '';
    const centerKey  = centerName || `order-${app?.orderId}`;

    const [existing, setExisting] = useState(() => getReviewForCenter(centerKey));
    const [hovered,  setHovered]  = useState(0);
    const [stars,    setStars]    = useState(0);
    const [comment,  setComment]  = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = () => {
        if (stars === 0) { setError('Please select a star rating.'); return; }
        setError('');
        const result = saveReview(centerKey, stars, comment, centerName);
        setExisting(result);
        setSubmitted(true);
    };

    if (existing?.userReview || submitted) {
        const review = existing?.userReview;
        return (
            <div className="csc-review csc-review--done">
                <div className="csc-review__section-label">
                    <RateReviewOutlinedIcon style={{ fontSize: '0.9rem' }} />
                    Your Review
                </div>
                <div className="csc-review__submitted">
                    <div className="csc-review__stars-row">
                        {[1,2,3,4,5].map((i) => (
                            <StarIcon key={i} className={`csc-review__star${i <= (review?.rating || 0) ? ' csc-review__star--on' : ' csc-review__star--off'}`} />
                        ))}
                        <span className="csc-review__rating-num">{review?.rating}/5</span>
                    </div>
                    {review?.comment && <p className="csc-review__comment-text">"{review.comment}"</p>}
                    <span className="csc-review__date">
                        Reviewed on {fmtReviewDate(review?.date)} · {centerName || 'CSC Center'}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="csc-review">
            <div className="csc-review__section-label">
                <RateReviewOutlinedIcon style={{ fontSize: '0.9rem' }} />
                Rate & Review — {centerName || 'CSC Center'}
            </div>
            <div className="csc-review__body">
                <p className="csc-review__prompt">How was your experience with this service center?</p>

                <div className="csc-review__star-selector">
                    {[1,2,3,4,5].map((i) => (
                        <button
                            key={i}
                            className="csc-review__star-btn"
                            onMouseEnter={() => setHovered(i)}
                            onMouseLeave={() => setHovered(0)}
                            onClick={() => setStars(i)}
                            title={`${i} star${i > 1 ? 's' : ''}`}
                        >
                            {(hovered || stars) >= i
                                ? <StarIcon className="csc-review__star csc-review__star--on" />
                                : <StarBorderIcon className="csc-review__star csc-review__star--off" />
                            }
                        </button>
                    ))}
                    {stars > 0 && (
                        <span className="csc-review__stars-label">
                            {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][stars]}
                        </span>
                    )}
                </div>

                <textarea
                    className="csc-review__textarea"
                    placeholder="Share details about the service quality, waiting time, staff helpfulness… (optional)"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={3}
                    maxLength={500}
                />
                <div className="csc-review__textarea-footer">
                    <span className="csc-review__char-count">{comment.length}/500</span>
                </div>

                {error && <p className="csc-review__error">{error}</p>}

                <button className="csc-review__submit" onClick={handleSubmit}>
                    Submit Review
                </button>
            </div>
        </div>
    );
};

// ── Owner completion documents ────────────────────────────────────────────────
const downloadOwnerDoc = (doc) => {
    if (!doc?.content) return;
    const a = document.createElement('a');
    a.href = doc.content;
    a.download = doc.fileName || doc.name || 'document';
    document.body.appendChild(a);
    a.click();
    a.remove();
};

const viewOwnerDoc = (doc) => {
    if (!doc?.content) return;
    const w = window.open();
    if (w) {
        w.document.write(`<html><body style="margin:0"><iframe src="${doc.content}" style="width:100%;height:100vh;border:none"></iframe></body></html>`);
        w.document.close();
    }
};

const fmtDocDate = (iso) => {
    try {
        return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
};

const OwnerDocumentsSection = ({ ownerDocuments }) => {
    if (!ownerDocuments?.length) return null;
    return (
        <div className="owner-docs-section">
            <div className="owner-docs-section__header">
                <TaskAltOutlinedIcon className="owner-docs-section__header-icon" />
                <div>
                    <h3 className="owner-docs-section__title">Documents from CSC Center</h3>
                    <p className="owner-docs-section__subtitle">
                        Your service has been completed. The CSC center has shared the following documents.
                    </p>
                </div>
            </div>
            <ul className="owner-docs-section__list">
                {ownerDocuments.map((doc, idx) => (
                    <li key={idx} className="owner-docs-section__item">
                        <div className="owner-docs-section__item-info">
                            <span className="owner-docs-section__item-name">{doc.name || doc.fileName || `Document ${idx + 1}`}</span>
                            {doc.uploadedAt && (
                                <span className="owner-docs-section__item-date">Uploaded {fmtDocDate(doc.uploadedAt)}</span>
                            )}
                        </div>
                        <div className="owner-docs-section__item-actions">
                            {doc.content && (
                                <>
                                    <button
                                        className="owner-docs-section__btn owner-docs-section__btn--view"
                                        onClick={() => viewOwnerDoc(doc)}
                                        title="View document"
                                    >
                                        <OpenInNewOutlinedIcon fontSize="small" />
                                        View
                                    </button>
                                    <button
                                        className="owner-docs-section__btn owner-docs-section__btn--download"
                                        onClick={() => downloadOwnerDoc(doc)}
                                        title="Download document"
                                    >
                                        <DownloadForOfflineOutlinedIcon fontSize="small" />
                                        Download
                                    </button>
                                </>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const detailsSectionSx = (theme) => ({
    backgroundColor: 'primary.main',
    marginBottom: `-${theme.tokens.spacing[16]}`,
});

const detailsSectionTextSx = { color: 'text.secondary' };

const STATUS_LABEL = {
    'applied':     'Applied',
    'in-progress': 'In Progress',
    'in progress': 'In Progress',
    'inprogress':  'In Progress',
    'completed':   'Completed',
    'rejected':    'Rejected',
    'cancelled':   'Cancelled',
};
const normalizeStatus = (raw) =>
    STATUS_LABEL[(raw || '').trim().toLowerCase()] || raw || '—';

const getApplicationFromStorage = (orderId) => {
    try {
        const apps = JSON.parse(localStorage.getItem('userApplications') || '[]');
        const app = apps.find((a) => String(a.orderId) === String(orderId));
        if (!app) return null;
        const normStatus = normalizeStatus(app.serviceStatus);
        // Also normalise every statusHistory entry so timeline labels are consistent
        const normHistory = (app.statusHistory || []).map((h) => ({
            ...h, status: normalizeStatus(h.status),
        }));
        const normApp = { ...app, serviceStatus: normStatus, statusHistory: normHistory };
        return {
            _raw: normApp,
            orderId: app.orderId,
            ownerDocuments: app.ownerDocuments || [],
            applicationDetails: {
                applicationName: app.serviceName,
                applicationStatus: normStatus,
                amount: app.amount,
                applicationSubmissionDate: app.applicationSubmissionDate,
                applicationLastUpdatedDate: app.applicationLastUpdatedDate,
                applicationFinalDate: app.applicationFinalDate,
            },
            cscCenterDetails: {
                cscCenterName: app.cscCenter?.cscCenterName || '',
                cscCenterAddress: app.cscCenter?.cscCenterAddress || '',
                cscCenterMobile: app.cscCenter?.cscCenterMobile || '',
                cscCenterEmail: app.cscCenter?.cscCenterEmail || '',
            },
            docuemntAccessList: {
                documentList: app.uploadedDocuments?.length ? app.uploadedDocuments : ['No documents uploaded'],
            },
            actions: {
                removeDocumentAccress: 'Yes/No',
                cancelApplication: 'Yes/No',
            },
        };
    } catch { return null; }
};

const AppliedApplicationDetails = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const currentApplication = getApplicationFromStorage(orderId);
    const [viewDeleteDialog, setViewDeleteDialog] = useState(false);
    const [cancepApplicationDialog, setCancepApplicationDialog] = useState(false);

    if (!currentApplication) {
        return (
            <div className="application-details-container" style={{ padding: '2rem', textAlign: 'center' }}>
                <p>Application not found.</p>
            </div>
        );
    }

    const backToAppsList = () => {
        navigate(-1)
    }
    const removeDocumentsAccess = () => {
        try {
            const apps = JSON.parse(localStorage.getItem('userApplications') || '[]');
            const updated = apps.map((a) =>
                String(a.orderId) === String(orderId)
                    ? { ...a, uploadedDocuments: [] }
                    : a
            );
            localStorage.setItem('userApplications', JSON.stringify(updated));
        } catch { /* storage full */ }
        navigate(0); // reload page to reflect change
    };

    const cancelApplication = () => {
        try {
            const apps = JSON.parse(localStorage.getItem('userApplications') || '[]');
            const now = new Date().toISOString();
            const updated = apps.map((a) => {
                if (String(a.orderId) !== String(orderId)) return a;
                return {
                    ...a,
                    serviceStatus: 'Cancelled',
                    lastUpdated: now,
                    applicationLastUpdatedDate: now,
                    statusHistory: [
                        ...(a.statusHistory || []),
                        { status: 'Cancelled', timestamp: now, updatedBy: 'Citizen', comment: 'Application cancelled by applicant.' },
                    ],
                };
            });
            localStorage.setItem('userApplications', JSON.stringify(updated));
        } catch { /* storage full */ }
        navigate(-1);
    };
    const isCompleted = currentApplication._raw?.serviceStatus === 'Completed';
    const isCancelledOrDone =
        currentApplication._raw?.serviceStatus === 'Cancelled' ||
        currentApplication._raw?.serviceStatus === 'Completed';

    const renderSection = (key, values) => {
        if (key === 'actions') {
            return (
                <div className="detail-block detail-block--actions" key={key}>
                    <List sx={detailsSectionSx} className="details-section">
                        <ListItem>
                            <ListItemText primary={applicationDetailsHeaderMapping[key]} sx={detailsSectionTextSx} />
                        </ListItem>
                    </List>
                    <div className="actions-card">
                        <button
                            className={`action-btn action-btn--remove${!isCompleted ? ' action-btn--disabled' : ''}`}
                            onClick={() => isCompleted && setViewDeleteDialog(true)}
                            disabled={!isCompleted}
                        >
                            <span className="action-btn__icon">🗂️</span>
                            <span className="action-btn__label">Remove Documents</span>
                            {!isCompleted && <span className="action-btn__hint">Available after completion</span>}
                        </button>
                        <button
                            className={`action-btn action-btn--cancel${isCancelledOrDone ? ' action-btn--disabled' : ''}`}
                            onClick={() => !isCancelledOrDone && setCancepApplicationDialog(true)}
                            disabled={isCancelledOrDone}
                        >
                            <span className="action-btn__icon">✕</span>
                            <span className="action-btn__label">Cancel Application</span>
                            {isCancelledOrDone && <span className="action-btn__hint">No longer available</span>}
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className="detail-block" key={key}>
                <List sx={detailsSectionSx} className="details-section">
                    <ListItem>
                        <ListItemText primary={applicationDetailsHeaderMapping[key]} sx={detailsSectionTextSx} />
                    </ListItem>
                </List>
                <div className="application-details-data-container">
                    {Object.entries(values).map((item) => (
                        <div className="application-details-data" key={applicationDetailMapping[item[0]]}>
                            <div className="application-detail-label">
                                {applicationDetailMapping[item[0]]}
                            </div>
                            <div className="application-detail-value">
                                {formatApplicationDetailsData(item[0], item[1])}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="application-details-container">
            <div className="application-details-header">
                <span className="header-back-icon">
                    <span className="icon-back" onClick={backToAppsList}><ArrowBackIosNewIcon /></span>
                    <span className="header-text"> Detailed Overview</span>
                </span>
                <span className="header-order"> {applicationDetailsHeaderMapping['orderId']} : {currentApplication['orderId']} </span>
            </div>

            <StatusTimeline app={currentApplication._raw} />

            {/* Owner completion documents — shown when CSC center has uploaded any */}
            {currentApplication.ownerDocuments?.length > 0 && (
                <OwnerDocumentsSection ownerDocuments={currentApplication.ownerDocuments} />
            )}

            {/* Balanced masonry — blocks pack into two columns without big gaps */}
            <div className="details-two-col">
                {renderSection('applicationDetails', currentApplication.applicationDetails)}
                {renderSection('docuemntAccessList', currentApplication.docuemntAccessList)}
                {renderSection('cscCenterDetails', currentApplication.cscCenterDetails)}
                {renderSection('actions', currentApplication.actions)}
                {currentApplication._raw?.serviceStatus === 'Completed' && (
                    <CscReview app={currentApplication._raw} />
                )}
            </div>

            <DeleteDialog
                setViewDeleteDialog={setViewDeleteDialog}
                viewDeleteDialog={viewDeleteDialog}
                deleteAction={removeDocumentsAccess}
                leftButtonTitle={APP_LABEL['en'].leftBtnTitle}
                confirmationMessage={APP_LABEL['en'].removeDocument}
                rightButtonTitle={APP_LABEL['en'].rightBtnTitle}
            />
            <DeleteDialog
                setViewDeleteDialog={setCancepApplicationDialog}
                viewDeleteDialog={cancepApplicationDialog}
                deleteAction={cancelApplication}
                leftButtonTitle={APP_LABEL['en'].leftBtnTitle}
                confirmationMessage={APP_LABEL['en'].cancelApplication}
                rightButtonTitle={APP_LABEL['en'].rightBtnTitle}
            />
        </div>
    );
}
export default AppliedApplicationDetails;